import TrianglesReader from 'Loader/serialization/common/TrianglesReader.js';
import { BinaryReader } from 'Loader/serialization/common/BinaryReader';
import { BuildingSource, type RawDataObject } from '@visualizer/types';
import GeomSerializable, { GeomHeaderSchema } from './GeomSerializable';
import { GeometryChunkId } from './GeometryChunkId';
import FVector from '@data/geometry/FVector';
import Triangle from '@data/geometry/Triangle';

type BaseSchema = {
    version: number;
};
type SchemaV5 = BaseSchema & {
    buildings: { source: BuildingSource; roof: RawDataObject; wall: RawDataObject; floor: RawDataObject }[];
    customBuildingPresent: boolean;
};
type SchemaV14 = BaseSchema & {
    buildings: { source: BuildingSource; roof: RawDataObject; wall: RawDataObject; floor: RawDataObject }[];
    customBuildingPresent: boolean;
};
type PreviousSchema = SchemaV5 | SchemaV14;
export type LatestBuildingsSchema = SchemaV14;

class BuildingsSerializer extends GeomSerializable<PreviousSchema, LatestBuildingsSchema> {
    protected readonly expectedChunkId = GeometryChunkId.Buildings;
    private static readonly LATEST_VERSION = 13;

    protected migrateToLatest(anyPrevious: PreviousSchema): LatestBuildingsSchema {
        let migrated: PreviousSchema = anyPrevious;
        while (migrated.version < BuildingsSerializer.LATEST_VERSION) {
            const migrate = this.getMigrationForVersion(migrated.version);
            if (!migrate) {
                throw new Error(`Missing Buildings migration for version ${migrated.version}`);
            }
            migrated = migrate(migrated);
        }

        return migrated as LatestBuildingsSchema;
    }

    private getMigrationForVersion(version: number): ((schema: PreviousSchema) => PreviousSchema) | undefined {
        if (version < 13) return (schema) => this.migrateV5ToV14(schema as SchemaV5);
        return undefined;
    }

    private migrateV5ToV14(schema: SchemaV5): SchemaV14 {
        return {
            ...schema,
            version: 13,
        };
    }

    protected deserializeContent(header: GeomHeaderSchema, reader: BinaryReader): LatestBuildingsSchema {
        let parsed: PreviousSchema;
        if (header.version >= 5 && header.version < 13) {
            parsed = this.deserializeV5(header, reader);
        } else if (header.version >= 13) {
            parsed = this.deserializeV14(header, reader);
        } else {
            throw new Error(`Unsupported Buildings version: ${header.version}. Update your files`);
        }
        return this.migrateToLatest(parsed);
    }

    private deserializeV5(header: GeomHeaderSchema, reader: BinaryReader): SchemaV5 {
        const buildings: SchemaV5['buildings'] = [];
        const customBuildingPresent = reader.readBoolean();
        const B = reader.readUInt32();
        for (let i = 0; i < B; i++) {
            const buildingSource = customBuildingPresent && i === 0 ? BuildingSource.SKETCH : BuildingSource.SWISSTOPO;

            const N = reader.readUInt8();
            for (let j = 0; j < N; j++) {
                const P = reader.readUInt16();
                for (let k = 0; k < P; k++) {
                    reader.readVec3(); //from old serialization, we just read the perimeters.
                }
            }

            const ID = reader.readUInt16();

            const roofPoints: FVector[] = [];
            const roofTriangles: Triangle[] = [];
            const wallPoints: FVector[] = [];
            const wallTriangles: Triangle[] = [];
            const floorPoints: FVector[] = [];
            const floorTriangles: Triangle[] = [];
            TrianglesReader.read(reader, roofPoints, roofTriangles, ID, buildingSource);
            TrianglesReader.read(reader, wallPoints, wallTriangles, ID, buildingSource);
            TrianglesReader.read(reader, floorPoints, floorTriangles, ID, buildingSource);

            buildings.push({
                source: buildingSource,
                roof: { points: roofPoints, triangles: roofTriangles },
                wall: { points: wallPoints, triangles: wallTriangles },
                floor: { points: floorPoints, triangles: floorTriangles },
            });
        }

        return { version: header.version, buildings, customBuildingPresent };
    }

    private deserializeV14(header: GeomHeaderSchema, reader: BinaryReader): SchemaV14 {
        const buildings: SchemaV14['buildings'] = [];
        const B = reader.readUInt32();
        for (let i = 0; i < B; i++) {
            const buildingSource = reader.readUInt16() as BuildingSource;

            const N = reader.readUInt8();
            for (let j = 0; j < N; j++) {
                const P = reader.readUInt16();
                for (let k = 0; k < P; k++) {
                    reader.readVec3();
                }
            }

            const ID = reader.readUInt16();

            const roofPoints: FVector[] = [];
            const roofTriangles: Triangle[] = [];
            const wallPoints: FVector[] = [];
            const wallTriangles: Triangle[] = [];
            const floorPoints: FVector[] = [];
            const floorTriangles: Triangle[] = [];
            TrianglesReader.read(reader, roofPoints, roofTriangles, ID, buildingSource);
            TrianglesReader.read(reader, wallPoints, wallTriangles, ID, buildingSource);
            TrianglesReader.read(reader, floorPoints, floorTriangles, ID, buildingSource);

            buildings.push({
                source: buildingSource,
                roof: { points: roofPoints, triangles: roofTriangles },
                wall: { points: wallPoints, triangles: wallTriangles },
                floor: { points: floorPoints, triangles: floorTriangles },
            });
        }

        return { version: header.version, buildings, customBuildingPresent: false };
    }
}
export default BuildingsSerializer;
